import { Link } from "react-router-dom";
import { Droplets, Sun, Leaf, ArrowRight, Phone, Award, Check } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import LastUpdated from "../components/LastUpdated";
import FAQ from "../components/FAQ";
import ContactForm from "../components/ContactForm";
import { COMPANY, IMAGES, PAGE_UPDATED, webPageLd, breadcrumbLd } from "../data/site";

const principles = [
  "Plan & design — zone the yard by water needs",
  "Improve the soil — 3\" compost tilled 6\" deep",
  "Use drought-tolerant native plants",
  "Limit turf to functional areas only",
  "Efficient drip irrigation + smart controller",
  "Mulch 3\" deep — hardwood or DG",
  "Right-fit seasonal maintenance",
];

const plants = [
  { type: "Trees", list: "Texas Red Oak · Cedar Elm · Texas Mountain Laurel · Anacacho Orchid" },
  { type: "Shrubs", list: "Cenizo · Esperanza · Flame Acanthus · Dwarf Yaupon" },
  { type: "Perennials", list: "Salvia greggii · Blackfoot Daisy · Damianita · Mealy Blue Sage" },
  { type: "Grasses", list: "Mexican Feather Grass · Lindheimer Muhly · Big Muhly · Sea Oats" },
  { type: "Succulents", list: "Agave parryi · Yucca · Red Yucca · Sotol · Prickly Pear" },
  { type: "Groundcover", list: "Silver Ponyfoot · Frogfruit · Horseherb (shade)" },
];

export default function XeriscapePage() {
  return (
    <>
      <Seo
        title="Xeriscape & Drought-Tolerant Landscaping Austin TX | TSB"
        description="Xeriscape design and installation in Austin TX. Native plants, drip irrigation, Austin Water rebates. Cut your water bill 50–60%. Free design consultation."
        canonical={`${window.location.origin}/xeriscape`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Xeriscape Design & Installation",
            provider: { "@type": "GeneralContractor", name: "Texan Strong Builds" },
            areaServed: "Austin, TX",
            description: "Drought-tolerant landscape design, native plant installation, drip irrigation conversion, decomposed granite hardscape and Austin Water rebate management.",
          },
          webPageLd({
            name: "Xeriscape Austin TX | Drought-Tolerant Landscaping | Texan Strong Builds",
            path: "/xeriscape",
            updated: PAGE_UPDATED.xeriscape,
            description: "Xeriscape design and installation in Austin TX by Texan Strong Builds.",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Xeriscape", path: "/xeriscape" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative bg-stone-900 text-white pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.xeriscape2} alt="Austin xeriscape with native plants" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/65 to-stone-950/35" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Breadcrumbs dark items={[{ name: "Home", path: "/" }, { name: "Xeriscape", path: "/xeriscape" }]} />
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-[10px] uppercase tracking-[0.22em] font-bold mb-7">
              <Leaf size={12} /> Our most-requested service
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Xeriscape <span className="text-emerald-400">Austin.</span>
              <br /> Beautiful. Drought-proof.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-stone-200 max-w-2xl">
              Native Texas plants. Drip irrigation. Decomposed-granite paths. Stone borders.
              Texan Strong Builds designs Austin, TX xeriscape yards to thrive through Stage 3
              watering restrictions — and qualify for{" "}
              <strong className="text-white">Austin Water rebates up to $3,000</strong>.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="btn-primary" data-testid="xeriscape-page-cta">
                Free design consultation <ArrowRight size={18} />
              </Link>
              <a href={COMPANY.phoneTel} className="btn-ghost-dark">
                <Phone size={18} /> {COMPANY.phone}
              </a>
            </div>
            <LastUpdated dark date={PAGE_UPDATED.xeriscape} className="mt-6" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-emerald-700 text-white border-y border-emerald-800">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
          {[
            { v: "58%", l: "avg. water savings" },
            { v: "$3,000", l: "max Austin Water rebate" },
            { v: "5–8 yr", l: "typical payback" },
            { v: "365", l: "days of beauty" },
          ].map((s, i) => (
            <div key={i} className="px-5 py-10 text-center border-r last:border-r-0 border-emerald-800 even:border-r-0 md:even:border-r">
              <div className="font-display text-4xl md:text-5xl font-black tracking-tighter">{s.v}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-emerald-200">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Principles + plants */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">
              The TSB xeriscape method
            </div>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tighter text-stone-900 leading-tight">
              Seven principles. One thriving Austin yard.
            </h2>
            <ul className="mt-8 space-y-3">
              {principles.map((p) => (
                <li key={p} className="flex items-start gap-3 text-stone-700 text-base">
                  <span className="mt-1 bg-emerald-600 text-white p-0.5"><Check size={14} /></span>
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-8 p-6 border-l-4 border-red-600 bg-stone-50">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-red-600">
                <Award size={14} /> Rebate help included
              </div>
              <p className="mt-2 text-stone-700">
                Texan Strong Builds prepares all Austin Water rebate paperwork, measurements
                and photo documentation for you — no city forms to figure out.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">
              Native &amp; adapted plant palette
            </div>
            <h2 className="mt-3 font-display text-4xl font-black tracking-tighter text-stone-900 leading-tight">
              What we plant in Austin.
            </h2>
            <div className="mt-8 border border-stone-200 divide-y divide-stone-200">
              {plants.map((p) => (
                <div key={p.type} className="p-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-stone-500">
                    {p.type}
                  </div>
                  <div className="mt-1 font-semibold text-stone-900">{p.list}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FAQ headline="Xeriscape & landscaping FAQs for Austin homeowners" />
      <ContactForm compact />
    </>
  );
}
